<template>
    <v-card
        :class="[
            'period-card h-full transition-all duration-300 hover:shadow-lg',
        ]"
        :data-period="period"
        elevation="2"
    >
        <v-card-title
            class="d-flex align-center justify-space-between mb-4 bg-gray-200 py-2"
        >
            <div class="w-full text-center">
                <h3
                    :class="isMain ? 'text-h6' : 'text-h7'"
                    class="font-weight-bold"
                >
                    {{ formatPeriodName(period) }}
                </h3>
            </div>
        </v-card-title>

        <v-card-text>
            <!-- Charts Section -->
            <div v-if="hasChartData" class="charts-section">
                <!-- Two Column Layout for Year and Half periods -->
                <div class="charts-layout flex flex-wrap gap-4 justify-center">
                    <!-- Left Column: Stars Distribution -->
                    <div class="stats-column flex flex-col justify-center gap-10" style="flex: 0 0 30%">
                        <div class="flex flex-col gap-2 text-center">
                            <div class="text-[4rem] font-extrabold text-[#1976d2]">
                                {{
                                    periodData.basicData.participants_percentage
                                }}
                                %
                            </div>
                        </div>
                        <div
                            class="flex justify-center"
                            v-if="periodData.starsData.length > 0 || true"
                        >
                            <table class="">
                                <thead>
                                    <tr class="border-b">
                                        <th class="p-1 text-center">التقييم</th>
                                        <th class="p-1 text-center">
                                            عدد المشاركات
                                        </th>
                                        <th class="p-1 text-center">
                                            عدد التعليقات
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="star in [5, 4, 3, 2, 1]"
                                        :key="star"
                                        class="border-b"
                                    >
                                        <td class="py-3">
                                            <div
                                                class="d-flex align-center justify-center"
                                            >
                                                <div class="stars-display">
                                                    <span
                                                        v-for="i in star"
                                                        :key="i"
                                                        class="star-icon text-[#2f71b3]"
                                                        :class="
                                                            shouldUseLargerStars
                                                                ? 'icon-[material-symbols--star] size-5'
                                                                : 'icon-[material-symbols--star] size-4'
                                                        "
                                                    ></span>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="py-3 text-center">
                                            <span class="font-bold">
                                                {{
                                                    getStarData(star)
                                                        .participants_count
                                                }}
                                                مشاركة
                                            </span>
                                        </td>
                                        <td class="py-3 text-center">
                                            <span class="font-bold">
                                                {{
                                                    getStarData(star)
                                                        .comments_count
                                                }}
                                                تعليق
                                            </span>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td class="py-3 text-center">
                                            <span class="font-bold">
                                                المجموع
                                            </span>
                                        </td>

                                        <td class="py-3 text-center">
                                            <span class="font-bold">
                                                {{
                                                    periodData.basicData
                                                        .participants_count
                                                }}
                                                مشاركة
                                            </span>
                                        </td>

                                        <td class="py-3 text-center">
                                            <span class="font-bold">
                                                {{
                                                    periodData.basicData
                                                        .comments_count
                                                }}
                                                تعليق
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="text-red-600 text-center font-bold text-4">
                            التعليقات هي الاقتراحات والملاحظات التي يكتبها المستفيد عند إرسال المشاركة (التقييم)
                        </div>
                    </div>

                    <!-- Right Column: Dissatisfaction Chart -->
                    <div class="chart-column" style="flex: 1">
                        <div
                            class="h-full  flex flex-col justify-evenly"
                        >
                            <h4 class="text-subtitle-1 font-weight-medium mb-2 ">
                                أسباب عدم الرضا
                            </h4>
                            <div class="basis-full" >
                                <Bar
                                    :key="chartKey"
                                    class="!w-full "
                                    :data="reasonsChartData"
                                    :options="reasonsChartOptions"
                                />
                            </div>
                            <div class="text-red-600 mt-2 text-center font-bold h-10 text-4">
                                الأرقام تدل على عدد المشاركات التي لديها عدم رضى في كل سبب
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="py-8 text-center">
                <v-icon size="48" color="grey-lighten-2">mdi-chart-line</v-icon>
                <p class="text-body-2 text-grey-lighten-1 mt-2">
                    لا توجد بيانات متاحة لهذه الفترة
                </p>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup lang="ts">
import type { Period } from '@/types/survey'
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { computed, onBeforeUnmount, onMounted, ref, withDefaults } from 'vue';
import { Bar } from 'vue-chartjs';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    ChartDataLabels,
);

interface PeriodData {
    basicData: {
        participants_count: number;
        participants_percentage: number;
        comments_count: number;
    };
    starsData: Array<{
        stars_count: 1 | 2 | 3 | 4 | 5;
        participants_count: number;
        comments_count: number;
    }>;
    reasonsData: Array<{
        reason_name: string;
        count: number;
    }>;
}

const props = withDefaults(
    defineProps<{
        period: Period;
        periodData: PeriodData;
        isMain?: boolean;
        isDark?: boolean;
    }>(),
    {
        isMain: false,
        isDark: false,
    },
);

const chartKey = ref(0);

const forceChartRerender = () => {
    chartKey.value += 1;
};

onMounted(() => {
    window.addEventListener('resize', forceChartRerender);
    window.addEventListener('orientationchange', forceChartRerender);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', forceChartRerender);
    window.removeEventListener('orientationchange', forceChartRerender);
});

const hasChartData = computed(
    () =>
        props.periodData.starsData.length > 0 ||
        props.periodData.reasonsData.length > 0,
);

const shouldUseLargerStars = computed(
    () =>
        props.period === 'year' ||
        props.period === 'half1' ||
        props.period === 'half2' ||
        [
            'january',
            'february',
            'march',
            'april',
            'may',
            'june',
            'july',
            'august',
            'september',
            'october',
            'november',
            'december',
        ].includes(props.period),
);

const formatPeriodName = (period: Period): string => {
    const periodNames: Record<Period, string> = {
        year: 'القياس السنوي',
        half1: 'قياس النصف الأول',
        half2: 'قياس النصف الثاني',
        quarter1: 'قياس الربع الأول',
        quarter2: 'قياس الربع الثاني',
        quarter3: 'قياس الربع الثالث',
        quarter4: 'قياس الربع الرابع',
        january: 'قياس شهر يناير',
        february: 'قياس شهر فبراير',
        march: 'قياس شهر مارس',
        april: 'قياس شهر أبريل',
        may: 'قياس شهر مايو',
        june: 'قياس شهر يونيو',
        july: 'قياس شهر يوليو',
        august: 'قياس شهر أغسطس',
        september: 'قياس شهر سبتمبر',
        october: 'قياس شهر أكتوبر',
        november: 'قياس شهر نوفمبر',
        december: 'قياس شهر ديسمبر',
    };
    return periodNames[period];
};

const axisColor = computed(() => (props.isDark ? '#e5e7eb' : '#374151'));
const gridColor = computed(() =>
    props.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(75,85,99,0.2)',
);

// Reasons Chart Data
const reasonsChartData = computed(() => {
    const colors = [
        'rgba(255, 99, 132, 0.8)',
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 205, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)',
    ];

    return {
        labels: props.periodData.reasonsData.map((r) => r.reason_name),
        datasets: [
            {
                label: 'العدد',
                data: props.periodData.reasonsData.map((r) => r.count),
                backgroundColor: colors.slice(
                    0,
                    props.periodData.reasonsData.length,
                ),
                borderColor: colors
                    .slice(0, props.periodData.reasonsData.length)
                    .map((color) => color.replace('0.8', '1')),
                borderWidth: 2,
            },
        ],
    };
});

const reasonsChartOptions = computed(() => ({
    responsive: false,
    maintainAspectRatio: false,
    animation: {
        duration: 1500,
        easing: 'easeInOutQuart' as const,
    },
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            rtl: true,
            textDirection: 'rtl',
            callbacks: {
                title: (context: any) => context[0].label,
                label: (context: any) => `العدد: ${context.parsed.y}`,
            },
        },
        datalabels: {
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            borderRadius: 4,
            padding: 2,
            borderWidth: 0,
            textAlign: 'center',
            anchor: 'center',
            align: 'center',
            clamp: true,
            clip: true,
            offset: 0,
            font: {
                weight: 'bold' as const,
                size: 11,
            },
            formatter: (value: unknown) => {
                const numericValue = typeof value === 'number' ? value : Number(value) || 0;
                return numericValue;
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {
                stepSize: 1,
                color: axisColor.value,
            },
            grid: { color: gridColor.value },
        },
        x: {
            ticks: {
                maxRotation: 45,
                minRotation: 0,
                color: axisColor.value,
            },
            grid: { display: false },
        },
    },
}));

// Helper methods for stars display
const getStarData = (star: number) => {
    const starData = props.periodData.starsData.find(
        (s) => s.stars_count === star,
    );
    return starData || { participants_count: 0, comments_count: 0 };
};
</script>

<style scoped>
.period-card {
    border-radius: 12px !important;
    overflow: hidden;
    background: white;
    border: none;
}

.main-card {
    border: 2px solid #1976d2;
    background: white;
}

.period-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.charts-section {
    animation: slideUp 0.8s ease-out;
}

.charts-layout {
    align-items: stretch;
}

.charts-layout .stats-column {
    flex: 0 0 30%;
    min-width: 260px;
}

.charts-layout .chart-column {
    flex: 1 1 0%;
    min-width: 280px;
}

@media (max-width: 900px) {
    .charts-layout {
        flex-direction: column;
    }

    .charts-layout .stats-column,
    .charts-layout .chart-column {
        flex: 1 1 100%;
        min-width: 100%;
    }

    .charts-layout table {
        width: 100%;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.stars-display {
    display: flex;
    justify-content: center;
    direction: ltr;
    flex-wrap: nowrap;
    gap: 2px;
}

.star-icon {
    flex-shrink: 0;
}

.primary--text {
    color: #1976d2 !important;
}

.success--text {
    color: #4caf50 !important;
}

.info--text {
    color: #2196f3 !important;
}
</style>
